import * as React from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import MediaQuery from 'react-responsive'
import { AppContext, AppContextProps } from 'src/AppContext'

import HamburgerIcon from '@uniiverse/library/assets/icons/hamburger.svg'
import SearchIcon from '@uniiverse/library/assets/icons/search.svg'
import Logo from '@uniiverse/library/assets/universe.svg'

import { Avatar } from 'src/components/Avatar/Avatar'
import { BREAK_MD } from 'src/lib/media-queries'
import { signInPath } from 'src/lib/navigate-to'

import { MobileDrawer } from './MobileDrawer'
import { HostEventsDropdown, UserDropdown, HelpDropdown } from './Dropdowns'
import { SearchBar } from './SearchBar'
import { locale } from './locale'

const style = require('./Navbar.css')

interface Props {
  sticky?: boolean;
  transparent?: boolean;
  shadow?: boolean;
  className?: string;
  toggleSearch?: () => void;
  showSearchBar?: boolean;
}

const DesktopLinks = ({ viewer, intl }) => {
  const [userDropdownShown, setUserDropdownShown] = React.useState(false)
  const [hostEventsDropdownShown, setHostEventsDropdownShown] = React.useState(false)
  const [helpDropdownShown, setHelpDropdownShown] = React.useState(false)

  const toggleUserDropdown = () => {
    setHostEventsDropdownShown(false)
    setUserDropdownShown(!userDropdownShown)
  }

  const toggleHostEventsDropdown = () => {
    setUserDropdownShown(false)
    setHelpDropdownShown(false)
    setHostEventsDropdownShown(!hostEventsDropdownShown)
  }

  const toggleHelpDropdown = (evt) => {
    evt.preventDefault();
    setHostEventsDropdownShown(false)
    setHelpDropdownShown(!helpDropdownShown);
  }

  const renderDropdown = viewer ? (
    <React.Fragment>
      <div
        className={style.viewer}
        onClick={toggleUserDropdown}
        onKeyPress={toggleUserDropdown}
        role="button"
        tabIndex={-1}
      >
        <div>
          <Avatar user={viewer} />
          <UserDropdown shown={userDropdownShown} viewer={viewer} intl={intl} />
        </div>
        <span className={style.name}>{viewer.firstName}</span>
      </div>
    </React.Fragment>
  ) : (
      <React.Fragment>
        <div className={style.link}>
          <a href="/help" onClick={toggleHelpDropdown}>{intl.formatMessage(locale.help)}</a>
          <HelpDropdown shown={helpDropdownShown} intl={intl} />
        </div>
        <div className={style.link}>
          <a className="unii-skip" href={signInPath()}>{intl.formatMessage(locale.logIn)}</a>
        </div>
        <div className={style.link}>
          <a href="/sign-up">{intl.formatMessage(locale.signUp)}</a>
        </div>
      </React.Fragment>
  )

  return (
    <div className={style.rightSide}>
      <div className={style.link}>
        <a href="/explore">{intl.formatMessage(locale.findEvents)}</a>
      </div>
      {
        viewer && viewer.host ?
          <div className={style.link}>
            <a href="/create">{intl.formatMessage(locale.createEvent)}</a>
          </div>
          :
          <React.Fragment>
            <div className={style.link}>
              <a className="unii-skip" href="#" onClick={toggleHostEventsDropdown}>{intl.formatMessage(locale.hostEvents)}</a>
              <HostEventsDropdown shown={hostEventsDropdownShown} intl={intl} />
            </div>
          </React.Fragment>
      }
      {renderDropdown}
    </div>
  )
}

const NavbarComponent: React.FunctionComponent<Props & InjectedIntlProps> = ({
  className,
  sticky,
  transparent,
  shadow,
  toggleSearch = () => {},
  showSearchBar = false,
  intl,
}) => {
  const [mobileDrawerShown, setMobileDrawerShown] = React.useState(false)
  const { viewer }: AppContextProps = React.useContext(AppContext)
  const toggleMobileDrawer = () => setMobileDrawerShown(!mobileDrawerShown)

  const containerClassName = [
    style.container,
    className || '',
    transparent ? style.transparent : '',
    sticky ? style.sticky : '',
    shadow ? style.shadow : '',
  ].join(' ')

  return (
    <div id="navbar" className={containerClassName}>
      <div className={style.content}>
        <MediaQuery maxWidth={BREAK_MD - 1}>
          {showSearchBar && <SearchIcon className={style.search} onClick={toggleSearch} />}
          <div className={`${style.logoContainer} ${showSearchBar ? style.showSearchBar : ''}`}>
            <a href="/" aria-label={intl.formatMessage(locale.linkToHomePage)}><Logo /></a>
          </div>
          <HamburgerIcon className={style.hamburger} onClick={toggleMobileDrawer} />
          <MobileDrawer shown={mobileDrawerShown} onClose={toggleMobileDrawer} intl={intl} viewer={viewer} />
        </MediaQuery>
        <MediaQuery minWidth={BREAK_MD}>
          <div className={style.logoContainer}><a href="/" aria-label={intl.formatMessage(locale.linkToHomePage)}><Logo /></a></div>
          {showSearchBar && <SearchBar />}
          <DesktopLinks viewer={viewer} intl={intl} />
        </MediaQuery>
      </div>
    </div>
  )
}

export const Navbar = injectIntl(NavbarComponent)
